import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import PinGate from './components/PinGate';
import ErrorBoundary from './components/ErrorBoundary';
import IntakeApp from './intake/IntakeApp';
import { ClientData, BLANK_CLIENT } from './types/ClientData';
import { saveSession, loadSession } from './utils/session';
import { SARAH_MITCHELL, CLAIRE_HENDERSON } from './data/demoClients';
import ClientLoader from './screens/ClientLoader';
import CoaMapper from './screens/admin/CoaMapper';
import SessionOneIntro from './screens/SessionOneIntro';
import SessionTwoIntro from './screens/SessionTwoIntro';
import SessionThreeIntro from './screens/SessionThreeIntro';

// ── Existing screens ─────────────────────────────────────────────────────────
import ClientSetup from './screens/ClientSetup';
import IncomeGrossToNet from './screens/IncomeGrossToNet';
import MonthlyWaterfall from './screens/MonthlyWaterfall';
import EmergencyFund from './screens/EmergencyFund';
import Investments from './screens/Investments';
import FifteenPercentCheck from './screens/FifteenPercentCheck';

// ── Setup screens ────────────────────────────────────────────────────────────
import VoyantCaptureScreen from './screens/VoyantCapture';

// ── Position screens (Brief 12 Section 4) ────────────────────────────────────
import MonthlyPosition from './screens/MonthlyPosition';
import BalanceSheet from './screens/BalanceSheet';
import Benchmarking from './screens/Benchmarking';

// ── Position screens (Brief 34) ──────────────────────────────────────────────
import IncomeScreen from './screens/IncomeScreen';
import TaxScreen from './screens/TaxScreen';
import AllocationScreen from './screens/AllocationScreen';

// ── Gap screens (Brief 12 Section 5) ─────────────────────────────────────────
import RetirementStandard from './screens/RetirementStandard';
import FreedomNumberReveal from './screens/FreedomNumberReveal';
import TheGap from './screens/TheGap';
import WealthMultiplier from './screens/WealthMultiplier';

// ── Framework screen (Brief 12 Section 6) ────────────────────────────────────
import BabySteps from './screens/BabySteps';
import SavingsRateTable from './screens/SavingsRateTable';
import OMBFramework from './screens/OMBFramework';

// ── Your Plan screens (Brief 12 Section 7) ───────────────────────────────────
import DebtLever from './screens/DebtLever';
import PlanDividendReduction from './screens/PlanDividendReduction';
import PlanEmergencyFund from './screens/PlanEmergencyFund';
// PlanDebtSnowball removed — replaced by DebtLever (Brief 46B S5)
import PlanMortgageIsa from './screens/PlanMortgageIsa_archived';
import PlanMortgage from './screens/PlanMortgage';
import PlanISA from './screens/PlanISA';
import PlanPhasedPlan from './screens/PlanPhasedPlan';
import MilestonesWealth from './screens/MilestonesWealth';
import MilestonesGrowth from './screens/MilestonesGrowth';
import MilestonesFreedom from './screens/MilestonesFreedom';

// ── Close screens (Brief 12 Section 8) ───────────────────────────────────────
import VoyantBreakPrompt2 from './screens/VoyantBreakPrompt2';
import PostLeversComparison from './screens/PostLeversComparison';
import WhatTodayChanged from './screens/WhatTodayChanged';
import StandingOrders from './screens/StandingOrders';
import Session2Bridge from './screens/Session2Bridge';
import AccumulationTimeline from './screens/close/AccumulationTimeline';

// ── Running Tally (Brief 12 Section 3) ───────────────────────────────────────
import RunningTally from './components/RunningTally';
import RunningTallyS2 from './components/RunningTallyS2';

// ── Session 2 screens (Brief 74 rebuild) ─────────────────────────────────
import S1Review from './screens/s2/S1Review';
import BusinessPL from './screens/s2/BusinessPL';
import DirectorExtraction from './screens/s2/DirectorExtraction';
import BusinessTrends from './screens/s2/BusinessTrends';
import SalesMarginAnalysis from './screens/s2/SalesMarginAnalysis';
import CapitalAllocation from './screens/s2/CapitalAllocation';
import CTPosition from './screens/s2/CTPosition';
import PensionLever from './screens/s2/PensionLever';
import ValueGapCalculator from './screens/s2/ValueGapCalculator';
import PLProjection from './screens/s2/PLProjection';
import BalanceSheetProjection from './screens/s2/BalanceSheetProjection';
import SessionProgress from './screens/s2/SessionProgress';
import WhatS2Changed from './screens/s2/WhatS2Changed';
import S2StandingOrders from './screens/s2/S2StandingOrders';
import BusinessActionPlan from './screens/s2/BusinessActionPlan';
import S3Bridge from './screens/s2/S3Bridge';

// ── Session 3 screens (Brief 18) ────────────────────────────────────────────
import EarlierRetirement from './screens/s3/EarlierRetirement';
import PhasedRetirement from './screens/s3/PhasedRetirement';
import OneOffSpend from './screens/s3/OneOffSpend';
import StressTest from './screens/s3/StressTest';
import Longevity from './screens/s3/Longevity';
import LifestyleBusiness from './screens/s3/LifestyleBusiness';
import LifeDesign from './screens/s3/LifeDesign';
import VoyantConfirm from './screens/s3/VoyantConfirm';
import ProgrammeSummary from './screens/s3/ProgrammeSummary';
import AnnualReview from './screens/s3/AnnualReview';

// ── Brief 26 new screens ───────────────────────────────────────────────────
import ClientWelcome from './screens/ClientWelcome';
// TodayVsTarget removed — merged into FreedomNumberReveal (Brief 46A S7)
import ProgrammeMap from './components/ProgrammeMap';

// ── Shared calculations ────────────────────────────────────────────────────
import { calcFreedomNumber } from './utils/freedomNumberCalc';

// ── Sidebar data entry tools ────────────────────────────────────────────────
import TwelveMonthPlan from './screens/TwelveMonthPlan';
import WealthTrajectory from './screens/WealthTrajectory';
import DrawdownAnalysis from './screens/DrawdownAnalysis';
import Income from './screens/Income';
import AssetsDebts from './screens/AssetsDebts';
import Assumptions from './screens/Assumptions';
import ToolRentalAssets from './screens/ToolRentalAssets';
import ToolFamilyGoals from './screens/ToolFamilyGoals';
import ToolSession3Setup from './screens/ToolSession3Setup';

// ── Re-export types for backward compatibility with old screens ──────────────
export type { ClientData } from './types/ClientData';

export interface ScreenProps {
  data: ClientData;
  setData: React.Dispatch<React.SetStateAction<ClientData>>;
}

// ── Screen component prop types ──────────────────────────────────────────────

interface NewScreenProps {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// ── Movement & screen definitions ────────────────────────────────────────────

type MovementId =
  | 'setup' | 'position' | 'gap' | 'framework' | 'yourplan' | 'close'
  | 's2_position' | 's2_gap' | 's2_levers' | 's2_cash' | 's2_close'
  | 's3_scenarios' | 's3_lifedesign' | 's3_confirm' | 's3_summary' | 's3_review';

interface ScreenDef {
  id: string;
  label: string;
  shortLabel: string;
  render: (data: ClientData, setData: React.Dispatch<React.SetStateAction<ClientData>>, onUpdate: (u: Partial<ClientData>) => void) => React.ReactNode;
  condition?: (data: ClientData) => boolean; // if present, screen only shown when true
}

interface MovementDef {
  id: MovementId;
  label: string;
  screens: ScreenDef[];
  isLocked?: (data: ClientData) => string | null; // returns lock reason or null
}

// ── Helper: wrap old-style screens (ScreenProps) ─────────────────────────────
function oldScreen(
  Comp: React.FC<ScreenProps>,
  id: string,
  label: string,
  shortLabel: string,
): ScreenDef {
  return {
    id, label, shortLabel,
    render: (data, setData) => <Comp data={data} setData={setData} />,
  };
}

// ── Helper: wrap new-style screens (data + onUpdate) ─────────────────────────
function newScreen(
  Comp: React.FC<NewScreenProps>,
  id: string,
  label: string,
  shortLabel: string,
): ScreenDef {
  return {
    id, label, shortLabel,
    render: (data, _setData, onUpdate) => <Comp data={data} onUpdate={onUpdate} />,
  };
}

// (placeholder helper removed — all movements now have real screens)

// Helper: trigger the Next button in the footer
function triggerNext() {
  const btn = document.querySelector('[data-nav="next"]') as HTMLElement | null;
  btn?.click();
}

// ── Movement definitions ─────────────────────────────────────────────────────

const MOVEMENTS: MovementDef[] = [
  // ── SETUP — adviser pre-session, client not present ────────────────────────
  {
    id: 'setup',
    label: 'Setup',
    screens: [
      {
        id: 'SessionOneIntro',
        label: 'Session 1 Intro',
        shortLabel: 'Intro',
        render: () => <SessionOneIntro onBegin={triggerNext} />,
      },
      newScreen(VoyantCaptureScreen, 'VoyantCapture', 'Pre-Session Setup', 'Setup'),
    ],
  },
  // ── POSITION — 5 screens (Brief 26: ClientWelcome added) ─────────────────
  {
    id: 'position',
    label: 'Position',
    screens: [
      {
        id: 'ClientWelcome',
        label: 'Welcome',
        shortLabel: 'Welcome',
        render: (data) => <ClientWelcome data={data} onBegin={triggerNext} sessionNumber={1} />,
      },
      newScreen(IncomeScreen, 'IncomeScreen', 'Your Income', 'Income'),
      newScreen(TaxScreen, 'TaxScreen', 'Your Tax', 'Tax'),
      newScreen(AllocationScreen, 'AllocationScreen', 'Your Allocation', 'Allocation'),
      newScreen(BalanceSheet, 'BalanceSheet', 'Your Balance Sheet', 'Balance Sheet'),
    ],
  },
  // ── FRAMEWORK — Baby Steps context ──────────────────────────────────────────
  {
    id: 'framework',
    label: 'Framework',
    screens: [
      oldScreen(BabySteps, 'BabySteps', 'Baby Steps', 'Baby Steps'),
      newScreen(SavingsRateTable, 'SavingsRateTable', 'Savings Rate', 'Savings Rate'),
    ],
  },
  // ── GAP — 4 screens (Brief 46A S7: merged Today vs Target into Freedom Number) ─
  {
    id: 'gap',
    label: 'Gap',
    screens: [
      newScreen(RetirementStandard, 'RetirementStandard', 'Retirement Standard', 'RLSA'),
      newScreen(FreedomNumberReveal, 'FreedomNumberReveal', 'Your Freedom Number', 'Freedom #'),
      newScreen(TheGap, 'TheGap', 'The Gap', 'The Gap'),
      newScreen(WealthMultiplier, 'WealthMultiplier', 'Wealth Multiplier', 'Multiplier'),
    ],
  },
  // ── YOUR PLAN — 6 screens (live decision screens) ─────────────────────────
  {
    id: 'yourplan',
    label: 'Your Plan',
    screens: [
      newScreen(DebtLever, 'DebtLever', 'Debt', 'Debt'),                               // S1 — always visible (Brief 46B S5)
      newScreen(PlanEmergencyFund, 'PlanEmergencyFund', 'Savings', 'Savings'),           // S2
      newScreen(PlanDividendReduction, 'PensionContribution', 'Pension Contribution', 'Pension'), // S3
      newScreen(PlanISA, 'PlanISA', 'ISA', 'ISA'),                                      // S4
      newScreen(PlanMortgage, 'PlanMortgage', 'Mortgage', 'Mortgage'),                   // S5
      newScreen(PlanPhasedPlan, 'PlanPhasedPlan', 'Where Your Money Goes', 'Allocation'),  // S6
      newScreen(MilestonesWealth, 'MilestonesWealth', 'Wealth Trajectory', 'Wealth'),   // S7
    ],
  },
  // ── CLOSE — 6 screens ─────────────────────────────────────────────────────
  {
    id: 'close',
    label: 'Close',
    screens: [
      newScreen(PostLeversComparison, 'PostLeversComparison', 'What Changed Today', 'Comparison'),
      newScreen(AccumulationTimeline, 'AccumulationTimeline', 'Accumulation Timeline', 'Timeline'),
      newScreen(VoyantBreakPrompt2, 'VoyantUpdate', 'Voyant Update', 'Voyant'),
      newScreen(WhatTodayChanged, 'WhatTodayChanged', 'What Today Changed', "Today's Impact"),
      newScreen(StandingOrders, 'StandingOrders', 'Standing Orders', 'Standing Orders'),
      newScreen(Session2Bridge, 'Session2Bridge', 'Session 2 Bridge & Actions', 'S2 Bridge'),
    ],
  },
];

// ── Session 2 Movement definitions ────────────────────────────────────────────

// Screen order locked to Brief 74 — the 16 new screens plus the welcome + S2
// recap preamble. Movement group IDs kept stable (`s2_position`, `s2_gap`,
// `s2_levers`, `s2_cash`, `s2_close`) because RunningTallyS2 wiring below
// keys off them; only the human-facing labels and their contents changed.
const S2_MOVEMENTS: MovementDef[] = [
  {
    id: 's2_position',
    label: 'Position',
    screens: [
      {
        id: 'S2ClientWelcome',
        label: 'Welcome',
        shortLabel: 'Welcome',
        render: (data) => <ClientWelcome data={data} onBegin={triggerNext} sessionNumber={2} />,
      },
      {
        id: 'SessionTwoIntro',
        label: 'Session 2 Recap',
        shortLabel: 'Recap',
        render: (data) => <SessionTwoIntro clientData={data} onBegin={triggerNext} />,
      },
      newScreen(S1Review,           'S1Review',           'Session 1 Review',          'S1 Review'),
      newScreen(BusinessPL,         'BusinessPL',         'P&L Waterfall',             'P&L'),
      newScreen(DirectorExtraction, 'DirectorExtraction', 'Director Extraction',       'Extraction'),
      newScreen(BusinessTrends,     'BusinessTrends',     'Business Trends',           'Trends'),
    ],
  },
  {
    id: 's2_gap',
    label: 'Analysis',
    screens: [
      newScreen(SalesMarginAnalysis, 'SalesMarginAnalysis', 'Sales & Margin Analysis', 'Sales'),
    ],
  },
  {
    id: 's2_levers',
    label: 'Capital & Levers',
    screens: [
      newScreen(CapitalAllocation,   'CapitalAllocation',   'Capital Allocation',       'Capital'),
      newScreen(CTPosition,          'CTPosition',          'CT Position',              'CT'),
      newScreen(PensionLever,        'PensionLever',        'Pension Lever',            'Pension'),
      newScreen(ValueGapCalculator,  'ValueGapCalculator',  'Value Gap Calculator',     'Value Gap'),
    ],
  },
  {
    id: 's2_cash',
    label: 'Projection',
    screens: [
      newScreen(PLProjection,            'PLProjection',            'P&L Projection',          'P&L Proj'),
      newScreen(BalanceSheetProjection,  'BalanceSheetProjection',  'Balance Sheet Projection', 'BS Proj'),
    ],
  },
  {
    id: 's2_close',
    label: 'Close',
    screens: [
      newScreen(SessionProgress,    'SessionProgress',    'Session Progress',        'Progress'),
      newScreen(WhatS2Changed,      'WhatS2Changed',      'What S2 Changed',         "Today's Impact"),
      newScreen(S2StandingOrders,   'S2StandingOrders',   'S2 Standing Orders',      'SOs'),
      newScreen(BusinessActionPlan, 'BusinessActionPlan', 'Business Action Plan',    'Actions'),
      newScreen(S3Bridge,           'S3Bridge',           'Session 3 Bridge',        'S3 Bridge'),
    ],
  },
];

// ── Session 3 Movement definitions ────────────────────────────────────────────

const S3_MOVEMENTS: MovementDef[] = [
  {
    id: 's3_scenarios',
    label: 'Scenarios',
    screens: [
      {
        id: 'S3ClientWelcome',
        label: 'Welcome',
        shortLabel: 'Welcome',
        render: (data) => <ClientWelcome data={data} onBegin={triggerNext} sessionNumber={3} />,
      },
      {
        id: 'SessionThreeIntro',
        label: 'Session 3 Recap',
        shortLabel: 'Recap',
        render: (data) => <SessionThreeIntro clientData={data} onBegin={triggerNext} />,
      },
      {
        ...newScreen(EarlierRetirement, 'EarlierRetirement', 'Earlier Retirement', 'Earlier'),
        condition: (d: ClientData) => d.s3ScenarioEarlierOn,
      },
      {
        ...newScreen(PhasedRetirement, 'PhasedRetirement', 'Phased Retirement', 'Phased'),
        condition: (d: ClientData) => d.s3ScenarioPhasedOn,
      },
      {
        ...newScreen(OneOffSpend, 'OneOffSpend', 'One-Off Spend', 'One-Off'),
        condition: (d: ClientData) => d.s3ScenarioOneOffOn,
      },
      newScreen(StressTest, 'StressTest', 'Stress Test', 'Stress'),
      newScreen(Longevity, 'Longevity', 'Longevity', 'Longevity'),
      newScreen(LifestyleBusiness, 'LifestyleBusiness', 'Lifestyle Business', 'Lifestyle'),
    ],
  },
  {
    id: 's3_lifedesign',
    label: 'Life Design',
    screens: [
      newScreen(LifeDesign, 'LifeDesign', 'Life Design', 'Life Design'),
    ],
  },
  {
    id: 's3_confirm',
    label: 'Confirm',
    screens: [
      newScreen(VoyantConfirm, 'VoyantConfirm', 'Voyant Confirm', 'Confirm'),
    ],
  },
  {
    id: 's3_summary',
    label: 'Summary',
    screens: [
      newScreen(ProgrammeSummary, 'ProgrammeSummary', 'What the Programme Did', 'Summary'),
    ],
  },
  {
    id: 's3_review',
    label: 'Annual Review',
    screens: [
      newScreen(AnnualReview, 'AnnualReview', 'Annual Review', 'Review'),
    ],
  },
];

// ── Movement timing guides (tooltip only) ────────────────────────────────────
const MOVEMENT_TIMING: Record<string, string> = {
  // Session 1
  setup: '~5 min',
  position: '~15 min',
  gap: '~10 min',
  framework: '~5 min',
  yourplan: '~15 min',
  close: '~10 min',
  // Session 2
  s2_position: '~15 min',
  s2_gap: '~5 min',
  s2_levers: '~20 min',
  s2_cash: '~10 min',
  s2_close: '~10 min',
  // Session 3
  s3_scenarios: '~20 min',
  s3_lifedesign: '~10 min',
  s3_confirm: '~5 min',
  s3_summary: '~5 min',
  s3_review: '~10 min',
};

// ── Tool completion checker ──────────────────────────────────────────────────
function isToolPopulated(toolId: string, data: ClientData): boolean {
  switch (toolId) {
    case 'ClientSetup': return (data.clientName || '') !== '';
    case 'Income': return (data.salary || 0) > 0 || (data.dividends || 0) > 0;
    case 'AssetsDebts': return (data.pensionBalance || 0) > 0 || (data.mortgageBalance || 0) > 0;
    case 'Assumptions': return true; // always has defaults
    case 'MonthlyWaterfall': return (data.mortgagePayment || 0) > 0;
    case 'EmergencyFund': return (data.efAccount1Balance || 0) > 0 || (data.efAccount2Balance || 0) > 0;
    case 'Investments': return (data.isaBalance || 0) > 0 || (data.pensionBalance || 0) > 0;
    case 'FifteenPercentCheck': return (data.salary || 0) > 0;
    case 'TwelveMonthPlan': return (data.livingCosts || 0) > 0;
    case 'WealthTrajectory': return (data.pensionBalance || 0) > 0 || (data.isaBalance || 0) > 0;
    case 'DrawdownAnalysis': return (data.freedomNumberToday || 0) > 0;
    default: return false;
  }
}

// ── Existing utility screens (accessible from sidebar under "Tools") ─────────

const TOOL_SCREENS: ScreenDef[] = [
  oldScreen(ClientSetup, 'ClientSetup', 'Client Setup', 'Setup'),
  newScreen(Income, 'Income', 'Income', 'Income'),
  newScreen(AssetsDebts, 'AssetsDebts', 'Pensions & ISAs', 'Pensions'),
  newScreen(Assumptions, 'Assumptions', 'Assumptions', 'Assumptions'),
  oldScreen(MonthlyWaterfall, 'MonthlyWaterfall', 'Monthly Waterfall', 'Waterfall'),
  newScreen(ToolRentalAssets, 'ToolRentalAssets', 'Rental & Other Assets', 'Rental'),
  newScreen(ToolFamilyGoals, 'ToolFamilyGoals', 'Family & Goals', 'Family'),
  oldScreen(EmergencyFund, 'EmergencyFund', 'Emergency Fund', 'EF'),
  newScreen(Investments as React.FC<NewScreenProps>, 'Investments', 'Investments', 'Investments'),
  newScreen(FifteenPercentCheck as React.FC<NewScreenProps>, 'FifteenPercentCheck', '15% Check', '15% Check'),
  newScreen(TwelveMonthPlan, 'TwelveMonthPlan', '12-Month Plan', '12-Mo Plan'),
  newScreen(WealthTrajectory, 'WealthTrajectory', 'Wealth Trajectory', 'Trajectory'),
  newScreen(DrawdownAnalysis, 'DrawdownAnalysis', 'Drawdown Analysis', 'Drawdown'),
  newScreen(ToolSession3Setup, 'ToolSession3Setup', 'Session 3 Setup', 'S3 Setup'),
];

// ─────────────────────────────────────────────────────────────────────────────
// APP
// ─────────────────────────────────────────────────────────────────────────────

function MainApp() {
  const [clientData, setClientData] = useState<ClientData>({ ...BLANK_CLIENT });
  const [currentMovement, setCurrentMovement] = useState<MovementId>('setup');
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [taxYearChangedOnLoad, setTaxYearChangedOnLoad] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showTools, setShowTools] = useState(false);
  const [currentToolIndex, setCurrentToolIndex] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showDemoMenu, setShowDemoMenu] = useState(false);
  const [activeSession, setActiveSession] = useState<1 | 2 | 3>(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileHandleRef = useRef<FileSystemFileHandle | null>(null);
  const demoMenuRef = useRef<HTMLDivElement>(null);
  const isDirtyRef = useRef(false);
  const [isDirty, setIsDirty] = useState(false);

  // ── Before-state snapshot (set once on load, never updated by levers) ─────
  const [originalPensionContribution, setOriginalPensionContribution] = useState(0);
  const [originalIsaContribution, setOriginalIsaContribution] = useState(0);

  // ── Post-OAuth Xero redirect handler ──────────────────────────────────────
  // /api/xero/callback redirects to /?xero_connected=true&tenantId=...&tenantName=...
  // Bind the returned tenant onto the active client and strip the params so a
  // page refresh doesn't re-bind. xero_error surfaces as a toast.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xero_connected') === 'true') {
      const xeroTenantId = params.get('tenantId') || undefined;
      const xeroTenantName = params.get('tenantName') || undefined;
      if (xeroTenantId) {
        setClientData((prev) => ({ ...prev, xeroTenantId, xeroTenantName }));
        setToast(`Xero connected — ${xeroTenantName || xeroTenantId} bound to current client`);
      }
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('xero_error')) {
      setToast(`Xero connect failed: ${params.get('xero_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close demo menu on click outside
  React.useEffect(() => {
    if (!showDemoMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (demoMenuRef.current && !demoMenuRef.current.contains(e.target as Node)) {
        setShowDemoMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDemoMenu]);

  // ── Unsaved changes warning ───────────────────────────────────────────────
  React.useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // ── Pre-calculate Freedom Number so downstream screens have it ──
  useEffect(() => {
    const result = calcFreedomNumber(clientData);
    const diff = Math.abs(result.freedomNumberInflated - (clientData.freedomNumberInflated || 0));
    if (diff > 100) {
      setClientData(prev => ({
        ...prev,
        freedomNumberInflated: Math.round(result.freedomNumberInflated),
        freedomNumberToday: Math.round(result.freedomNumberToday),
      }));
    }
  }, [clientData.clientAge, clientData.targetFreedomAge, clientData.isCouple,
      clientData.plsaStandard, clientData.mortgageTermYears, clientData.mortgagePayment,
      clientData.statePensionAge, clientData.statePensionAnnual]);

  // ── Patch S1 close movement to pass snapshot props to PostLeversComparison ──
  const s1Movements = useMemo(() => {
    return MOVEMENTS.map(m => {
      if (m.id !== 'close') return m;
      return {
        ...m,
        screens: m.screens.map(s => {
          if (s.id !== 'PostLeversComparison') return s;
          return {
            ...s,
            render: (data: ClientData, _setData: React.Dispatch<React.SetStateAction<ClientData>>, onUpdate: (u: Partial<ClientData>) => void) => (
              <PostLeversComparison
                data={data}
                onUpdate={onUpdate}
                originalPensionContribution={originalPensionContribution}
                originalIsaContribution={originalIsaContribution}
              />
            ),
          };
        }),
      };
    });
  }, [originalPensionContribution, originalIsaContribution]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const activeMovements = activeSession === 3 ? S3_MOVEMENTS : activeSession === 2 ? S2_MOVEMENTS : s1Movements;
  const movementRaw = activeMovements.find(m => m.id === currentMovement) || activeMovements[0];
  // Filter out conditional screens that don't apply to this client
  const movementScreens = useMemo(() => {
    return movementRaw.screens.filter(s => !s.condition || s.condition(clientData));
  }, [movementRaw, clientData]);
  const movement = useMemo(() => ({ ...movementRaw, screens: movementScreens }), [movementRaw, movementScreens]);
  // When filtered screens change, preserve the current screen by ID rather than clamping by index.
  // This prevents conditional scenario screens toggling off from jumping the user to the wrong screen.
  const prevScreenIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevScreenIdRef.current) {
      const idx = movementScreens.findIndex(s => s.id === prevScreenIdRef.current);
      if (idx !== -1 && idx !== currentScreenIndex) {
        setCurrentScreenIndex(idx);
      }
    }
  }, [movementScreens]); // eslint-disable-line react-hooks/exhaustive-deps
  // Clamp index if conditional screens changed
  const safeScreenIndex = Math.min(currentScreenIndex, Math.max(0, movement.screens.length - 1));
  const screen = showTools && currentToolIndex !== null
    ? TOOL_SCREENS[currentToolIndex]
    : movement.screens[safeScreenIndex] || movement.screens[0];
  // Track current screen ID so we can find it again if the list refilters
  prevScreenIdRef.current = screen?.id ?? null;

  // ── Handlers ───────────────────────────────────────────────────────────────

  // Wrapped setData for old-style screens — marks dirty on any change
  const setClientDataDirty: React.Dispatch<React.SetStateAction<ClientData>> = useCallback((action) => {
    setClientData(action);
    isDirtyRef.current = true;
    setIsDirty(true);
  }, []);

  const handleUpdate = useCallback((updates: Partial<ClientData>) => {
    setClientData(prev => ({ ...prev, ...updates }));
    isDirtyRef.current = true;
    setIsDirty(true);
  }, []);

  const switchSession = useCallback((session: 1 | 2 | 3) => {
    setActiveSession(session);
    const firstMovement = session === 3 ? S3_MOVEMENTS[0] : session === 2 ? S2_MOVEMENTS[0] : MOVEMENTS[0];
    setCurrentMovement(firstMovement.id);
    setCurrentScreenIndex(0);
    setShowTools(false);
    setCurrentToolIndex(null);
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 2000);
  }, []);

  const handleSave = useCallback(async () => {
    await saveSession(clientData, fileHandleRef);
    isDirtyRef.current = false;
    setIsDirty(false);
    showToast('Session saved ✓');
  }, [clientData, showToast]);

  const handleLoadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await loadSession(file);
      setClientData(result.data);
      setOriginalPensionContribution(result.data.pensionCompanyFunded || 0);
      setOriginalIsaContribution(result.data.isaContribution || 0);
      setTaxYearChangedOnLoad(result.taxYearChanged);
      setCurrentMovement('setup');
      setCurrentScreenIndex(0);
      setShowTools(false);
      setCurrentToolIndex(null);
      setLoadError(null);
      fileHandleRef.current = null;  // loaded a different file — next Save opens picker
      isDirtyRef.current = false;
      setIsDirty(false);
      showToast(`Loaded ${result.data.clientName || 'client'}`);
    } catch (err) {
      setLoadError('Could not read file — is it a valid FFM session?');
      showToast('Could not read file');
    }
    e.target.value = '';
  }, [showToast]);

  const loadDemo = useCallback((demo: ClientData, name: string) => {
    setClientData({ ...demo, sessionDate: new Date().toLocaleDateString('en-GB') });
    setOriginalPensionContribution(demo.pensionCompanyFunded || 0);
    setOriginalIsaContribution(demo.isaContribution || 0);
    setCurrentMovement('setup');
    setCurrentScreenIndex(0);
    setShowTools(false);
    setCurrentToolIndex(null);
    setTaxYearChangedOnLoad(false);
    setShowDemoMenu(false);
    showToast(`Demo loaded — ${name}`);
  }, [showToast]);

  const handleNewClient = useCallback(() => {
    if (!window.confirm('Start a new client? All unsaved data will be lost.')) return;
    setClientData({ ...BLANK_CLIENT, sessionDate: new Date().toLocaleDateString('en-GB') });
    setCurrentMovement('setup');
    setCurrentScreenIndex(0);
    setShowTools(false);
    setCurrentToolIndex(null);
    setTaxYearChangedOnLoad(false);
    fileHandleRef.current = null;  // clear so next Save opens a fresh picker
    isDirtyRef.current = false;
    setIsDirty(false);
    showToast('New client — ready to go');
  }, [showToast]);

  // ── Navigation helpers ─────────────────────────────────────────────────────

  const navigateToMovement = (movId: MovementId) => {
    const mov = activeMovements.find(m => m.id === movId);
    if (!mov) return;
    const lockReason = mov.isLocked?.(clientData);
    if (lockReason) {
      showToast(lockReason);
      return;
    }
    setCurrentMovement(movId);
    setCurrentScreenIndex(0);
    setShowTools(false);
    setCurrentToolIndex(null);
  };

  const navigateToScreen = (screenIdx: number) => {
    setCurrentScreenIndex(screenIdx);
    setShowTools(false);
    setCurrentToolIndex(null);
  };

  const goNext = () => {
    if (showTools && currentToolIndex !== null) {
      if (currentToolIndex < TOOL_SCREENS.length - 1) {
        setCurrentToolIndex(currentToolIndex + 1);
      }
      return;
    }
    if (safeScreenIndex < movement.screens.length - 1) {
      setCurrentScreenIndex(safeScreenIndex + 1);
    } else {
      // Move to next movement
      const movIdx = activeMovements.findIndex(m => m.id === currentMovement);
      if (movIdx < activeMovements.length - 1) {
        const nextMov = activeMovements[movIdx + 1];
        const lockReason = nextMov.isLocked?.(clientData);
        if (lockReason) {
          showToast(lockReason);
          return;
        }
        setCurrentMovement(nextMov.id);
        setCurrentScreenIndex(0);
      }
    }
  };

  const goBack = () => {
    if (showTools && currentToolIndex !== null) {
      if (currentToolIndex > 0) {
        setCurrentToolIndex(currentToolIndex - 1);
      }
      return;
    }
    if (safeScreenIndex > 0) {
      setCurrentScreenIndex(safeScreenIndex - 1);
    } else {
      // Move to previous movement's last screen
      const movIdx = activeMovements.findIndex(m => m.id === currentMovement);
      if (movIdx > 0) {
        const prevMov = activeMovements[movIdx - 1];
        // Filter previous movement's screens for correct last index
        const prevScreens = prevMov.screens.filter(s => !s.condition || s.condition(clientData));
        setCurrentMovement(prevMov.id);
        setCurrentScreenIndex(prevScreens.length - 1);
      }
    }
  };

  const firstMovementId = activeMovements[0].id;
  const lastMovementId = activeMovements[activeMovements.length - 1].id;
  const isAtStart = !showTools && currentMovement === firstMovementId && currentScreenIndex === 0;
  const isAtEnd = !showTools && currentMovement === lastMovementId && currentScreenIndex === movement.screens.length - 1;

  // handleDemoFromHome removed — Brief 28 (ClientLoader replaces Home)

  // ── Render ─────────────────────────────────────────────────────────────────

  // Brief 28 — ClientLoader replaces Home as opening state
  const handleClientLoad = useCallback((data: ClientData) => {
    setClientData({ ...data, sessionDate: data.sessionDate || new Date().toLocaleDateString('en-GB') });
    isDirtyRef.current = true;
    setIsDirty(true);
  }, []);

  const handleSessionFromLoader = useCallback((session: 1 | 2 | 3) => {
    switchSession(session);
  }, [switchSession]);

  if (!clientData.clientName) {
    return <ClientLoader onLoad={handleClientLoad} onSessionSelect={handleSessionFromLoader} />;
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-ma-gray">
      {/* Hidden file input for Load */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* TOAST */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 bg-ma-navy text-white text-sm px-4 py-2.5 rounded-xl shadow-lg animate-fade-in">
          {toast}
        </div>
      )}

      {/* HEADER */}
      <header className="bg-ma-navy text-white flex items-center justify-between px-6 py-3 flex-shrink-0 shadow-lg relative z-50" style={{ overflow: 'visible' }}>
        {/* Left: logo */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-ma-gold rounded-sm flex items-center justify-center">
            <span className="text-ma-navy font-bold text-sm">M</span>
          </div>
          <div>
            <div className="font-semibold text-sm tracking-wide">Motion Accountancy</div>
            <div className="text-xs opacity-60 tracking-widest uppercase">Financial Freedom Model</div>
          </div>
        </div>

        {/* Centre: movement indicator */}
        <div className="flex items-center gap-1" style={{ position: 'relative', zIndex: 10 }}>
          {activeMovements.map((m) => {
            const isCurrent = m.id === currentMovement && !showTools;
            const lockReason = m.isLocked?.(clientData);
            return (
              <button
                key={m.id}
                onClick={(e) => { e.stopPropagation(); navigateToMovement(m.id); }}
                disabled={!!lockReason}
                title={lockReason || `${m.label} ${MOVEMENT_TIMING[m.id] || ''}`}
                style={{ cursor: lockReason ? 'not-allowed' : 'pointer' }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all select-none ${
                  isCurrent
                    ? 'bg-ma-gold text-ma-navy'
                    : lockReason
                    ? 'text-gray-500 opacity-40'
                    : 'text-blue-200 hover:text-white hover:bg-white/10'
                }`}
              >
                {lockReason ? '🔒 ' : ''}{m.label}
              </button>
            );
          })}
        </div>

        {/* Right: client name + save/load */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => { window.location.href = '/mapper'; }}
            className="text-gray-300 hover:text-white transition-colors text-base leading-none"
            title="COA Mapper"
            aria-label="Open chart of accounts mapper"
          >
            ⚙
          </button>

          <button
            onClick={handleNewClient}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-white/20 transition-colors"
            title="New Client"
          >
            New
          </button>

          {/* Demo dropdown */}
          <div className="relative" ref={demoMenuRef}>
            <button
              onClick={() => setShowDemoMenu(!showDemoMenu)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-white/20 transition-colors"
            >
              Demo ▾
            </button>
            {showDemoMenu && (
              <div className="absolute top-full right-0 mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50 text-gray-700">
                <button
                  onClick={() => loadDemo(SARAH_MITCHELL, 'Sarah Mitchell')}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50"
                >
                  <div className="font-medium">Sarah Mitchell</div>
                  <div className="text-xs text-gray-400">Simple case — basic rate</div>
                </button>
                <button
                  onClick={() => loadDemo(CLAIRE_HENDERSON, 'Claire Henderson')}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50"
                >
                  <div className="font-medium">Claire Henderson</div>
                  <div className="text-xs text-gray-400">Complex case — higher rate</div>
                </button>
              </div>
            )}
          </div>

          <button
            onClick={handleLoadClick}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-white/20 transition-colors"
          >
            Load
          </button>
          <button
            onClick={handleSave}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors relative ${
              isDirty
                ? 'bg-amber-400 text-ma-navy hover:bg-amber-300 animate-pulse'
                : 'bg-ma-teal text-ma-navy hover:bg-teal-300'
            }`}
          >
            Save
            {isDirty && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full border border-white" />
            )}
          </button>

          {/* Client name */}
          <div className="text-right min-w-28">
            {clientData.clientName ? (
              <div>
                <div className="text-sm font-medium">{clientData.clientName}</div>
                <div className="text-xs opacity-60">{clientData.businessName} · Session {activeSession}</div>
              </div>
            ) : (
              <div className="text-xs opacity-50 italic">No client loaded</div>
            )}
          </div>
        </div>
      </header>

      {/* TAX YEAR MIGRATION BANNER */}
      {taxYearChangedOnLoad && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <span className="text-amber-500 font-bold">⚠</span>
            Tax rates updated since this file was saved — review the income screen.
          </div>
          <button
            onClick={() => setTaxYearChangedOnLoad(false)}
            className="text-amber-600 hover:text-amber-800 text-sm font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* SESSION SWITCHER */}
      <div className="bg-gray-50 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-center gap-2 py-2 px-4">
          {([
            { session: 1 as const, label: 'Session 1 — Personal Plan' },
            { session: 2 as const, label: 'Session 2 — Business Engine' },
            { session: 3 as const, label: 'Session 3 — Life Design' },
          ]).map(({ session, label }) => (
            <button
              key={session}
              onClick={() => switchSession(session)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                activeSession === session
                  ? 'bg-ma-navy text-white shadow-sm'
                  : 'border border-ma-navy/30 text-ma-navy hover:bg-ma-navy/5'
              }`}
            >
              {activeSession === session ? '● ' : '○ '}{label}
            </button>
          ))}
        </div>
      </div>

      {/* PROGRESS INDICATOR */}
      {!showTools && (
        <div className="bg-white border-b border-gray-100 text-center py-1.5 flex-shrink-0">
          <span className="text-[10px] text-gray-500">
            Step {activeMovements.findIndex(m => m.id === currentMovement) + 1} of {activeMovements.length} — {movement.label}
          </span>
        </div>
      )}

      {/* RUNNING TALLY S1 — visible in Your Plan and Close movements */}
      {activeSession === 1 && (currentMovement === 'yourplan' || currentMovement === 'close') && !showTools && (
        <RunningTally
          taxSavedAnnual={clientData.totalTaxSavedAnnual || 0}
          taxSavedCumulative={clientData.totalTaxSavedCumulative || 0}
          interestSaved={clientData.totalInterestSaved || 0}
          freedomAgeImprovement={clientData.freedomAgeImprovement || 0}
          gapClosed={clientData.totalGapClosed || 0}
          gapTotal={clientData.gapAmount || 0}
          highlightGap={currentMovement === 'yourplan'}
          doNothingAge={clientData.voyantDoNothing?.retirementAge || 0}
          targetFreedomAge={clientData.targetFreedomAge || 0}
        />
      )}

      {/* RUNNING TALLY S2 — visible in Profit Levers, Cash & CT, and Close movements */}
      {activeSession === 2 && (currentMovement === 's2_levers' || currentMovement === 's2_cash' || currentMovement === 's2_close') && !showTools && (
        <RunningTallyS2
          additionalProfitMonthly={clientData.s2AdditionalProfitMonthly || 0}
          ctSavedAnnual={clientData.s2CtSavedAnnual || 0}
          ctSavedCumulative={clientData.s2CtSavedCumulative || 0}
          pensionFundedMonthly={clientData.s2PensionFundedMonthly || 0}
          freedomAgeImprovement={clientData.s2FreedomAgeImprovement || 0}
        />
      )}

      {/* MAIN BODY */}
      <div className="flex flex-1 overflow-hidden">
        {/* SIDEBAR */}
        <aside className="w-52 bg-white border-r border-gray-200 flex flex-col overflow-y-auto flex-shrink-0">
          {/* Movement screens */}
          <div className="px-3 py-3 border-b border-gray-100">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {showTools ? 'Tools' : movement.label}
            </div>
          </div>
          <nav className="flex-1 py-2">
            {!showTools && movement.screens.map((s, index) => {
              const isActive = index === currentScreenIndex;
              return (
                <button
                  key={s.id}
                  onClick={() => navigateToScreen(index)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-2 transition-all text-sm ${
                    isActive
                      ? 'bg-ma-navy text-white'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 font-medium ${
                    isActive ? 'bg-ma-gold text-ma-navy' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {index + 1}
                  </span>
                  <span className="leading-tight text-xs">{s.shortLabel}</span>
                </button>
              );
            })}
            {showTools && TOOL_SCREENS.map((s, index) => {
              const isActive = index === currentToolIndex;
              const populated = isToolPopulated(s.id, clientData);
              return (
                <button
                  key={s.id}
                  onClick={() => setCurrentToolIndex(index)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-2 transition-all text-sm ${
                    isActive
                      ? 'bg-ma-navy text-white'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 font-medium ${
                    isActive ? 'bg-ma-gold text-ma-navy' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {index + 1}
                  </span>
                  <span className="leading-tight text-xs flex-1">{s.shortLabel}</span>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${populated ? 'bg-[#2d7a4a]' : 'bg-gray-300'}`} />
                </button>
              );
            })}
          </nav>

          {/* Tools toggle at bottom */}
          <div className="border-t border-gray-100 p-3">
            <button
              onClick={() => {
                if (showTools) {
                  setShowTools(false);
                  setCurrentToolIndex(null);
                } else {
                  setShowTools(true);
                  setCurrentToolIndex(0);
                }
              }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                showTools
                  ? 'bg-ma-navy text-white'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              }`}
            >
              {showTools ? '← Back to Movements' : '🔧 Data Entry Tools'}
            </button>
          </div>
        </aside>

        {/* CONTENT */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Screen title bar */}
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-lg font-semibold text-ma-navy">{screen.label}</h1>
                <div className="text-xs text-gray-400">
                  {showTools
                    ? `Tools · ${(currentToolIndex ?? 0) + 1} of ${TOOL_SCREENS.length}`
                    : `${movement.label} · Screen ${currentScreenIndex + 1} of ${movement.screens.length}`
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Screen content — wrapped in ErrorBoundary so a crash in any
              single screen falls back to a friendly card instead of
              unmounting the whole root. Resets on screen navigation via
              the screenKey prop. */}
          <div className="flex-1 overflow-y-auto">
            <ErrorBoundary
              screenKey={`${currentMovement}-${screen.id}`}
              screenLabel={screen.label}
              onBack={goBack}
            >
              {screen.render(clientData, setClientDataDirty, handleUpdate)}
            </ErrorBoundary>
          </div>

          {/* Navigation footer */}
          <div className="bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
            <button
              onClick={goBack}
              disabled={isAtStart && !showTools}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isAtStart && !showTools
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              ← Back
            </button>

            {/* Movement progress dots */}
            {!showTools && (
              <div className="flex gap-1.5 items-center">
                {activeMovements.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => navigateToMovement(m.id)}
                    className={`h-2 rounded-full transition-all cursor-pointer ${
                      m.id === currentMovement
                        ? 'w-8 bg-ma-navy'
                        : m.isLocked?.(clientData)
                        ? 'w-2 bg-gray-200'
                        : 'w-2 bg-ma-gold'
                    }`}
                    title={m.label}
                  />
                ))}
              </div>
            )}

            <button
              data-nav="next"
              onClick={goNext}
              disabled={isAtEnd && !showTools}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                isAtEnd && !showTools
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'bg-ma-navy text-white hover:bg-ma-navy-light'
              }`}
            >
              Next →
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  const isIntakeSubdomain = window.location.hostname.startsWith('intake.');

  return (
    <Routes>
      <Route path="/intake" element={<IntakeApp />} />
      <Route path="/mapper" element={<PinGate><CoaMapper /></PinGate>} />
      <Route
        path="*"
        element={
          isIntakeSubdomain
            ? <IntakeApp />
            : <PinGate><MainApp /></PinGate>
        }
      />
    </Routes>
  );
}

export default App;
